import {
  Component,
  Host,
  h,
  Prop,
  State,
  Watch,
  Element,
  Event,
  EventEmitter,
  Method
} from "@stencil/core";

import axios from "axios";

import { createParagraphsFromBrs } from "../../../../utils/string-utils";
import { StzhLocaleAdapter } from "../../../../index";

import { StzhVbzMajortickerLocalizedText } from "./stzh-vbz-majorticker.localization";

import {
  VBZ,
  StzhVbzMajortickerDisturbanceFetchedEvent,
  StzhVbzMajortickerDisturbance,
  VbzTickerInfoUrl
} from "../../../../index";

/**
 */
@Component({
  tag: "stzh-vbz-majorticker",
  styleUrl: "stzh-vbz-majorticker.scss",
  scoped: true
})
export class StzhVbzMajorticker {
  /** API URL (`{lang}` is replaced in the URL)  */
  @Prop() api: string = "https://fpbe.zvv.ch/restproxy/himsearch?format=json&accessId=unic&channels=ticker&additionalfields=tickers:VBZ&orderBy=LMOD_DESC&lang={lang}";

  /** Data to pass in directly (alternative for API call) */
  @Prop() data: any = "";
  private _data: any;

  /** More info link (`{lang}` and `{id}` are replaced in the URL) */
  @Prop() moreInfoLink: string = "https://zvv.ch/zvv/{lang}/fahrplan/vl.html?himIds={id}";

  /**
   * Date adapter, for custom parsing/formatting.
   * Must be object with a `parse` function which accepts a `string` and returns a `Date`,
   * and a `format` function which accepts a `Date` and returns a `string`.
   */
  @Prop() dateAdapter: StzhLocaleAdapter;

  /** Translation strings. */
  @Prop() localization: StzhVbzMajortickerLocalizedText;

  /** Overwrite ghettobox close time cookie option if needed */
  @Prop() showCloseKey: string;

  /** Whether ghettobox is shown (if has not been closed before) */
  @State() ghettoboxShown: boolean = true;

  @Element() element: HTMLStzhVbzMajortickerElement;

  /** Event when major disturbance was fetched */
  @Event() stzhDisturbanceFetched: EventEmitter<StzhVbzMajortickerDisturbanceFetchedEvent>;

  /** Return whether ghettobox is shown (if has not been closed before) */
  @Method()
  async getGhettoboxShown() {
    return this.ghettoboxShown;
  }

  /** Return fetched disturbance */
  @Method()
  async getDisturbance() {
    return this.disturbance;
  }

  @Watch("api")
  async apiWatcher(newValue: string) {
    if (!newValue) {
      return;
    }

    let apiUrl = this.api.replace(/\{lang\}/, this.localization.$locale)
    apiUrl = apiUrl + (apiUrl.includes("?") ? "&" : "?") + Date.now();

    // load data from API
    const { data } = await axios.get(apiUrl);

    this.data = data;
  }

  @Watch("dateAdapter")
  dateAdapterWatcher(newValue: StzhLocaleAdapter) {
    if (!newValue) {
      this.dateAdapter = window.stzhComponents.utils.createFormatParseAdapter(
        this.localization.$formats,
        this.localization.$globals
      );
    }
  }

  @Watch("data")
  async dataWatcher(newValue: any) {
    if (typeof newValue === "string") {
      this._data = JSON.parse(newValue);
    } else {
      this._data = newValue;
    }

    const bigDisturbance = this._data?.Message
      ?.find(message => message.category === VBZ.DISTURBANCE_INFORMATION && message.baseType === VBZ.BASE_TYPE_GLOBAL);

    if (bigDisturbance) {
      const urls: VbzTickerInfoUrl[] = bigDisturbance.channel
        .filter(channel => channel.name === VBZ.CHANNEL_TICKER && channel.url)
        .map(channel => channel.url)
        .reduce((a, b) => a.concat(b), []);

      const infoUrl = urls
        .find(url => url.url.indexOf(VBZ.CHANNEL_INFO_URL_FILTER) !== -1);

      this.disturbance = {
        id: bigDisturbance.id,
        head: bigDisturbance.head,
        text: createParagraphsFromBrs(bigDisturbance.text),
        startDate: new Date(`${bigDisturbance.sDate}T${bigDisturbance.sTime}+02:00`),
        endDate: new Date(`${bigDisturbance.eDate}T${bigDisturbance.eTime}+02:00`),
        altStart: bigDisturbance.altStart,
        altEnd: bigDisturbance.altEnd,
        infoUrl: infoUrl
      };
    }

    this.stzhDisturbanceFetched.emit({
      component: "stzh-vbz-majorticker",
      disturbance: this.disturbance
    })
  }

  private disturbance: StzhVbzMajortickerDisturbance = null;

  private onGhettoboxOpen = () => {
    this.ghettoboxShown = true;
  }

  private onGhettoboxClose = () => {
    this.ghettoboxShown = false;
  }

  async componentWillLoad() {
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "vbz-majorticker");
    }

    this.dateAdapterWatcher(this.dateAdapter);

    if (this.data) {
      await this.dataWatcher(this.data);
    } else {
      await this.apiWatcher(this.api);
    }
  }

  render() {
    const classes = {
      "stzh-vbz-majorticker": true
    };

    return (
      <Host hidden={!this.disturbance || !this.ghettoboxShown}>
        <div class={classes}>
          {this.disturbance &&
            <stzh-show
              mikro
              closeKey={this.showCloseKey}
              showFromDate={this.disturbance.startDate}
            >
              <stzh-ghettobox
                onStzhOpen={this.onGhettoboxOpen}
                onStzhClose={this.onGhettoboxClose}
                class="stzh-vbz-majorticker__ghettobox"
                hideClose={true}
                mainTitle={this.disturbance.head}
              >
                <stzh-richtext innerHTML={this.disturbance.text}></stzh-richtext>
                {this.disturbance.infoUrl ?
                  <stzh-button
                    slot="button"
                    href={this.disturbance.infoUrl.url}
                  >
                    {this.disturbance.infoUrl.name}
                  </stzh-button>
                :
                  <stzh-button
                    slot="button"
                    target="_blank"
                    href={this.moreInfoLink
                      .replace(/\{lang\}/, this.localization.$locale)
                      .replace(/\{id\}/, this.disturbance.id)}
                  >
                    {this.localization.moreInfo}
                  </stzh-button>
                }
              </stzh-ghettobox>
            </stzh-show>
          }
        </div>
      </Host>
    );
  }
}
